/*
 * The Contact form — the sidebar button above Settings, and the modal it opens.
 *
 * The message is POSTed to the `supportApi` Cloud Function, which queues it in
 * the same `mail` outbox as every other message the service sends and addresses
 * it to the operator. What the server does with it, and why that is an endpoint
 * rather than a `mailto:` link, is in functions/lib/support.js.
 *
 * WHY THIS DOES NOT USE services/functions-api.js. `callFunction` there refuses
 * outright when nobody is signed in — every one of its callers is an arbiter
 * doing something to their own tournament, so that is the right guard for them
 * and exactly the wrong one here. This form must work for a spectator with no
 * account and for a player who cannot get into theirs, so the token is attached
 * when there is one and simply left off when there is not.
 */

import { state } from '../state.js';
import { FUNCTIONS_BASE, currentLang } from '../services/functions-api.js';

const ENDPOINT = `${FUNCTIONS_BASE}/supportApi`;

/*
 * The server's error codes, mapped to what the person reading them should do.
 *
 * Translated here rather than server-side: the app already carries both
 * languages (js/i18n/no.js, keyed by the English source string), and a message
 * built here switches with the app's own language setting instead of with
 * whatever the request happened to say. A code we do not recognise falls back to
 * the server's own English text, and then to a generic failure.
 */
const ERROR_TEXT = {
    invalid_email: 'Enter an email address we can reply to.',
    empty_message: 'Write what you would like help with.',
    rate_limited: 'That is a few messages in a short time. Wait a little and try again — or write straight to misha@zugzwang.no.',
    internal_error: 'Something went wrong on our end. Please try again in a moment.',
    network: 'Could not reach us. Check your connection and try again — or write straight to misha@zugzwang.no.'
};

/** When the form was opened. The server refuses a submission filled impossibly fast. */
let openedAt = 0;

function els() {
    return {
        modal: document.getElementById('contact-modal'),
        form: document.getElementById('contact-form'),
        topic: document.getElementById('contact-topic'),
        name: document.getElementById('contact-name'),
        email: document.getElementById('contact-email'),
        message: document.getElementById('contact-message'),
        honeypot: document.getElementById('contact-hp'),
        error: document.getElementById('contact-error'),
        send: document.getElementById('btn-contact-send')
    };
}

function showError(el, text) {
    if (!el) return;
    el.textContent = text;
    el.style.display = text ? 'block' : 'none';
}

/** The screen the form was opened from, for "where were you when it happened". */
function activeViewId() {
    const view = document.querySelector('.view.active');
    return view ? view.id : '';
}

/**
 * The name we can offer to fill in, from the profile this device already has.
 * Never taken as fact — it goes into an editable field, and the message says
 * separately which account (if any) the server verified.
 */
function suggestedName() {
    const p = state.userProfile || {};
    const full = [p.givenName, p.lastName].filter(Boolean).join(' ').trim();
    return full || p.displayName || '';
}

function suggestedEmail() {
    try {
        const user = window.authApp && window.authApp.getCurrentUser && window.authApp.getCurrentUser();
        return (user && user.email) || '';
    } catch (e) {
        return '';
    }
}

/** The token, if this browser has one. Never a reason to fail — see the header. */
async function maybeToken() {
    try {
        if (!window.authApp || typeof window.authApp.getIdToken !== 'function') return null;
        return (await window.authApp.getIdToken()) || null;
    } catch (e) {
        return null;
    }
}

export function openContact() {
    const e = els();
    if (!e.modal) return;

    showError(e.error, '');
    if (e.honeypot) e.honeypot.value = '';

    // Prefilled every open rather than once, because who is signed in changes
    // while the page stays loaded. Anything already typed is left alone: a
    // message half-written, dismissed to check a detail and reopened, must
    // survive — losing it is how a bug report stops being sent.
    if (e.name && !e.name.value) e.name.value = suggestedName();
    if (e.email && !e.email.value) e.email.value = suggestedEmail();

    openedAt = Date.now();
    e.modal.classList.add('active');

    // The topic is the first choice to make, but the message is the reason
    // anyone is here — so focus goes there, with the topic one Shift+Tab away.
    if (e.message) setTimeout(() => e.message.focus(), 50);
}

export function closeContact() {
    const modal = document.getElementById('contact-modal');
    if (modal) modal.classList.remove('active');
}

/** Empty the form. Only after a message has actually gone. */
function resetForm() {
    const e = els();
    if (e.form) e.form.reset();
    showError(e.error, '');
}

async function submit() {
    const e = els();
    if (!e.form || !e.send) return;

    const email = (e.email.value || '').trim();
    const message = (e.message.value || '').trim();

    // Checked here as well as on the server, so the ordinary slip — a blank
    // message, a mistyped address — is answered instantly and without a round
    // trip. The server's check is the one that counts; this one is courtesy.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showError(e.error, tr(ERROR_TEXT.invalid_email));
        e.email.focus();
        return;
    }
    if (message.length < 5) {
        showError(e.error, tr(ERROR_TEXT.empty_message));
        e.message.focus();
        return;
    }

    showError(e.error, '');
    const originalLabel = e.send.innerHTML;
    e.send.disabled = true;
    e.send.textContent = tr('Sending…');

    const payload = {
        topic: e.topic ? e.topic.value : 'question',
        lang: currentLang(),
        email,
        name: (e.name.value || '').trim(),
        message,
        // Read from the honeypot rather than a form serialisation, so a browser
        // extension that strips hidden inputs cannot silently drop it.
        _hp: e.honeypot ? e.honeypot.value : '',
        // How long the form was open. The server reads this as an automation
        // signal; a person cannot describe a problem in under two seconds.
        elapsedMs: Date.now() - openedAt,
        context: {
            version: (typeof window.zugzwangBuild === 'function' && window.zugzwangBuild()) || '',
            lang: currentLang(),
            view: activeViewId(),
            userAgent: navigator.userAgent || ''
        }
    };

    let data;
    try {
        const token = await maybeToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(ENDPOINT, { method: 'POST', headers, body: JSON.stringify(payload) });
        data = await res.json().catch(() => ({}));
        if (!res.ok) data = Object.assign({ error: 'internal_error' }, data);
        else data = Object.assign({ ok: true }, data);
    } catch (err) {
        console.error('Contact message failed:', err);
        data = { error: 'network' };
    }

    e.send.disabled = false;
    e.send.innerHTML = originalLabel;

    if (data && data.ok) {
        closeContact();
        resetForm();
        window.Dialog.alert(
            tr('Thank you. Your message is on its way, and the answer will come to {email}.', { email }),
            tr('Message Sent'),
            'success'
        );
        return;
    }

    const code = (data && data.error) || 'internal_error';
    showError(e.error, tr(ERROR_TEXT[code] || (data && data.message) || ERROR_TEXT.internal_error));

    // Point at the field the server named, where it named one.
    const field = data && Array.isArray(data.fields) && data.fields[0] && data.fields[0].field;
    if (field === 'email' && e.email) e.email.focus();
    if (field === 'message' && e.message) e.message.focus();
}

/*
 * The generic `.btn-close-modal` handler in app.js closes a hard-coded list of
 * modals rather than "whichever modal this button is in", so this one wires its
 * own close paths — the same arrangement js/ui/privacy-policy.js uses.
 */
export function setupContact() {
    const openBtn = document.getElementById('btn-contact');
    if (openBtn) {
        openBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            openContact();

            // The sidebar is a drawer on mobile and stays over the page unless
            // something closes it, which would leave the modal behind it.
            const sidebar = document.querySelector('.sidebar');
            const backdrop = document.getElementById('sidebar-backdrop');
            if (sidebar && sidebar.classList.contains('active')) {
                sidebar.classList.remove('active');
                if (backdrop) backdrop.classList.remove('active');
            }
        });
    }

    const modal = document.getElementById('contact-modal');
    if (!modal) return;

    modal.querySelectorAll('.btn-close-contact').forEach(btn => {
        btn.addEventListener('click', closeContact);
    });

    modal.addEventListener('click', (ev) => {
        if (ev.target === modal) closeContact();
    });

    document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && modal.classList.contains('active')) closeContact();
    });

    const form = document.getElementById('contact-form');
    if (form) {
        form.addEventListener('submit', (ev) => {
            ev.preventDefault();
            submit();
        });
    }
}
